import { Link } from "react-router-dom";
import { AlertTriangle, ArrowUpRight, Boxes, ClipboardList, LifeBuoy, ShoppingBag, Users } from "lucide-react";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { PageHeader, Panel, StatusBadge, Tone } from "@/components/shared";
import { Progress } from "@/components/ui/progress";
import { inr, label, useApi } from "@/lib/api";

function Metric({ icon: Icon, title, value, to, testid, sub }) {
  return (
    <Link to={to} data-testid={testid} className="group rounded-lg border bg-card p-5 hover:border-foreground/20 transition-colors">
      <div className="flex items-center justify-between text-muted-foreground">
        <span className="text-xs font-medium uppercase tracking-wider">{title}</span>
        <Icon className="h-4 w-4" />
      </div>
      <div className="mt-3 text-3xl font-semibold num">{value}</div>
      <div className="mt-1 text-xs text-muted-foreground flex items-center gap-1">{sub}<ArrowUpRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" /></div>
    </Link>
  );
}

export default function Dashboard() {
  const { data } = useApi("/dashboard");
  if (!data) return null;
  const leadData = Object.entries(data.leadsByStatus).map(([k, v]) => ({ name: label(k), value: v }));
  return (
    <div>
      <PageHeader title="Dashboard" subtitle="Live snapshot of sales, production and support." />
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 fade-up">
        <Metric icon={Users} title="Active Leads" value={data.activeLeads} to="/leads" testid="metric-active-leads" sub="Open in pipeline" />
        <Metric icon={ClipboardList} title="Projects" value={data.projectsInPipeline} to="/projects" testid="metric-projects" sub="Active builds" />
        <Metric icon={AlertTriangle} title="Low Stock" value={data.lowStock.length} to="/inventory" testid="metric-low-stock" sub="≤ 3 units left" />
        <Metric icon={LifeBuoy} title="Open Tickets" value={data.openTickets} to="/tickets" testid="metric-open-tickets" sub="Awaiting resolution" />
        <Metric icon={ShoppingBag} title="Online Orders" value={data.onlineOrders} to="/online-orders" testid="metric-online-orders" sub="From Shopify" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
        <div className="rounded-lg border bg-card p-5"><div className="text-xs text-muted-foreground uppercase tracking-wider">Won Revenue</div>
          <div className="text-2xl font-semibold num mt-2" data-testid="metric-won-revenue">{inr(data.wonRevenue)}</div></div>
        <div className="rounded-lg border bg-card p-5"><div className="text-xs text-muted-foreground uppercase tracking-wider">Won Net Profit</div>
          <div className="text-2xl font-semibold num mt-2 text-emerald-600 dark:text-emerald-400" data-testid="metric-won-profit">{inr(data.wonProfit)}</div></div>
        <div className="rounded-lg border bg-card p-5"><div className="text-xs text-muted-foreground uppercase tracking-wider">Inventory Value</div>
          <div className="text-2xl font-semibold num mt-2" data-testid="metric-inventory-value">{inr(data.inventoryValue)}</div></div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mt-4">
        <Panel title="Lead Pipeline" className="lg:col-span-3 min-w-0">
          <div className="h-64 p-4">
            <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
              <BarChart data={leadData}>
                <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                <Tooltip cursor={{ fill: "hsl(var(--muted))" }} contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", fontSize: 12 }} />
                <Bar dataKey="value" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
        <Panel title="Low Stock Alerts" className="lg:col-span-2 min-w-0" action={<Boxes className="h-4 w-4 text-muted-foreground" />}>
          <div className="divide-y max-h-64 overflow-y-auto" data-testid="low-stock-list">
            {data.lowStock.map((p) => (
              <div key={p.id} className="flex items-center justify-between px-5 py-2.5 text-sm">
                <div className="min-w-0"><div className="truncate">{p.name}</div><div className="text-xs text-muted-foreground">{label(p.category)}</div></div>
                <Tone tone={p.stockQuantity === 0 ? "red" : "amber"}>{p.stockQuantity} left</Tone>
              </div>
            ))}
          </div>
        </Panel>
      </div>
      <Panel title="Projects in Pipeline" className="mt-4">
        <div className="divide-y">
          {data.recentProjects.map((p) => (
            <Link to={`/projects/${p.id}`} key={p.id} className="flex items-center gap-4 px-5 py-3 hover:bg-secondary/40 transition-colors" data-testid={`dash-project-${p.projectCode}`}>
              <span className="font-mono text-xs w-20">{p.projectCode}</span>
              <span className="text-sm flex-1 truncate">{p.customerName}</span>
              <span className="text-xs text-muted-foreground w-40 truncate hidden md:inline">{p.currentStageName}</span>
              <Progress value={p.progressPct} className="w-32 h-1.5 hidden sm:block" />
              <StatusBadge status={p.status} />
            </Link>
          ))}
        </div>
      </Panel>
    </div>
  );
}
